import { Form } from "react-bootstrap";
import { Icon, fas } from "../../../../component-library";
import { displayDateAndTime } from "../../../../helper-functions";
import { Link } from "react-router-dom";
import { useState, KeyboardEvent, useCallback, Dispatch, SetStateAction, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../../api";
import { usePermissions } from "../../../../contexts";
import { Claims } from "../../../../models";

interface DistributionDashboardHeaderProps {
    showMobileMetrics: boolean;
    includePending: boolean;
    includeSclera: boolean;
    setShowMobileMetrics: Dispatch<SetStateAction<boolean>>;
    setIncludePending: Dispatch<SetStateAction<boolean>>;
    setIncludeSclera: Dispatch<SetStateAction<boolean>>;
}

export function DistributionDashboardHeader({
    showMobileMetrics,
    includePending,
    includeSclera,
    setShowMobileMetrics,
    setIncludePending,
    setIncludeSclera,
}: Readonly<DistributionDashboardHeaderProps>) {
    const { hasPermission } = usePermissions();
    const queryClient = useQueryClient();

    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

    const refreshData = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.distribution() });
        setLastUpdated(new Date());
    }, [queryClient]);

    useEffect(() => {
        const frequency = 5 * 60 * 1000; // 5 minutes
        const interval = setInterval(() => {
            refreshData();
        }, frequency);
        return () => clearInterval(interval);
    }, [refreshData]);

    const handleRefreshKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            refreshData();
        }
    };

    return (
        <div className="distro-header px-3">
            <div className="d-flex justify-content-between">
                <div>
                    <h1 className="me-2">Distribution Dashboard</h1>
                    {hasPermission(Claims.ViewSurgery) && (
                        <Link to={"/surgery-calendar"} className="d-block d-lg-none">
                            View Surgery Calendar
                        </Link>
                    )}
                </div>
            </div>
            <div className="d-flex align-items-center">
                <Form.Check
                    type="checkbox"
                    id="showMobileMetrics"
                    label="Show Metrics"
                    className="mx-2 d-block d-lg-none"
                    checked={showMobileMetrics}
                    onChange={() => setShowMobileMetrics((prev) => !prev)}
                />
                <Form.Check
                    type="checkbox"
                    id="showPending"
                    label="Include Pending"
                    className="mx-2"
                    checked={includePending}
                    onChange={() => setIncludePending((prev) => !prev)}
                />
                <Form.Check
                    type="checkbox"
                    id="showSclera"
                    label="Include Sclera"
                    className="mx-2"
                    checked={includeSclera}
                    onChange={() => setIncludeSclera((prev) => !prev)}
                />
                <div className="d-none d-lg-flex align-items-center">
                    <Icon
                        icon={fas.faArrowsRotate}
                        className="text-success clickable me-1"
                        onClick={refreshData}
                        aria-label="Refresh data"
                        tabIndex={0}
                        onKeyDown={(e) => handleRefreshKeyDown(e)}
                    />{" "}
                    Last Updated {displayDateAndTime(lastUpdated.toISOString())}
                </div>
            </div>
        </div>
    );
}
